import { useCallback, useEffect, useRef, useState } from 'react'

const STORE_PREFIX = 'hw_draft_v2_'
const META_PREFIX = 'hw_draft_meta_'

export type DraftMeta = { savedAt: number }

/**
 * 表单暂存：切页/刷新都不丢配置。
 * 对应参考站的 rh_page_draft_v2_<routeKey> 机制。
 * 除了值本身，还记录 savedAt 用于在 UI 上显示「已自动保存 · 14:32」。
 */
export function useDraft<T extends Record<string, unknown>>(routeKey: string, initial: T) {
  const storeKey = `${STORE_PREFIX}${routeKey}`
  const metaKey = `${META_PREFIX}${routeKey}`

  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(storeKey)
      if (!raw) return initial
      const parsed = JSON.parse(raw) as Record<string, unknown>
      /**
       * 丢掉历史版本留下的保留键。
       *
       * 2026-10-05 上午短暂存在过一套「草稿结构迁移」（`__draftVersion` + `migrate`）。
       * 它当天就被撤掉了，原因值得钉在这里 —— **版本号必须是「值已经被迁移过」的证据，
       * 不能是「代码已经是新版」的声明**：
       *
       * 那套机制的判据是「存进去的版本号」，而版本号是**每次写盘都写**的。于是
       * 这个顺序会把它彻底打穿 —— HMR 更新代码时 React 保留着旧的 state（迁移函数
       * 根本没机会跑），紧接着任意一次 patch 触发 `persist`，就把**新版本号盖着旧值**
       * 写下去了。此后迁移永远免检，用户看到的是「改了参数、提示词纹丝不动」，
       * 而所有日志与断言都是绿的。
       *
       * 现在改用「手改版绑定输入指纹」自愈（见 `VideoStudio` 的 `sbInputKey`）：
       * 判据取自**当前输入**，不依赖任何落盘的版本号，因此 HMR / 手改 / 回滚都打不穿。
       * 这里只剩清理：读的时候丢掉，写回时自然消失。
       */
      delete parsed.__draftVersion
      return { ...initial, ...(parsed as T) }
    } catch {
      return initial
    }
  })

  const [savedAt, setSavedAt] = useState<number | null>(() => {
    const raw = localStorage.getItem(metaKey)
    return raw ? Number(raw) : null
  })

  const first = useRef(true)
  useEffect(() => {
    // 首次挂载不写回，避免把 initial 覆盖掉刚读出来的值
    if (first.current) {
      first.current = false
      return
    }
    const t = window.setTimeout(() => {
      try {
        localStorage.setItem(storeKey, JSON.stringify(value))
        const now = Date.now()
        localStorage.setItem(metaKey, String(now))
        setSavedAt(now)
      } catch {
        /* ignore */
      }
    }, 400)
    return () => window.clearTimeout(t)
  }, [storeKey, metaKey, value])

  const patch = useCallback((p: Partial<T>) => setValue((v) => ({ ...v, ...p })), [])

  const reset = useCallback(() => {
    setValue(initial)
    try {
      localStorage.removeItem(storeKey)
      localStorage.removeItem(metaKey)
    } catch {
      /* ignore */
    }
    setSavedAt(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeKey, metaKey])

  return { value, setValue, patch, reset, savedAt }
}
